import { useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { api } from '@/lib/api/client';
import { rolesApi, rolesQueryKeys } from '../api';
import type { Permission } from '../types';

export const roleSchema = z.object({
  name: z.string().min(1, 'nameRequired'),
  name_en: z.string().optional(),
  name_ru: z.string().optional(),
  status: z.enum(['ACTIVE', 'ARCHIVED'] as const),
  type: z.enum(['MANAGER', 'PARTNER', 'STAFF', 'LEAD', 'MD'] as const),
  rate: z.coerce.number().min(0).optional(),
  permissions: z.array(z.string()).optional(),
});

export type RoleFormValues = z.infer<typeof roleSchema>;

interface PermissionsResponse {
  results: Permission[];
  meta?: { total: number };
}

function groupPermissionsByResource(permissions: Permission[]) {
  const groups: Record<string, Permission[]> = {};

  permissions.forEach((perm) => {
    const parts = perm.name.split(':');
    const resource = parts.length > 1 ? parts[0] : 'other';
    if (!groups[resource]) {
      groups[resource] = [];
    }
    groups[resource].push(perm);
  });

  const sortedGroups: Record<string, Permission[]> = {};
  Object.keys(groups)
    .sort()
    .forEach((key) => {
      sortedGroups[key] = groups[key].sort((a, b) => a.name.localeCompare(b.name));
    });

  return sortedGroups;
}

export function useRoleForm(id?: number | string) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const isEdit = !!id;

  const form = useForm<RoleFormValues>({
    resolver: zodResolver(roleSchema),
    defaultValues: {
      name: '',
      name_en: '',
      name_ru: '',
      status: 'ACTIVE',
      type: 'MANAGER',
      rate: undefined,
      permissions: [],
    },
  });

  const selectedPermissions = form.watch('permissions') || [];

  // Fetch all permissions
  const { data: permissionsData, isLoading: isLoadingPermissions } = useQuery({
    queryKey: ['permissions', 'all'],
    queryFn: async () => {
      const res = await api.get<PermissionsResponse>('/permissions', {
        params: { limit: 500 },
      });
      return res.results || [];
    },
    enabled: open,
    staleTime: 5 * 60 * 1000,
  });

  // Group and filter permissions
  const groupedPermissions = useMemo(() => {
    if (!permissionsData) return {};

    const filtered = searchQuery
      ? permissionsData.filter((p) =>
          p.name.toLowerCase().includes(searchQuery.toLowerCase())
        )
      : permissionsData;

    return groupPermissionsByResource(filtered);
  }, [permissionsData, searchQuery]);

  const allPermissionNames = useMemo(() => {
    return permissionsData?.map((p) => p.name) || [];
  }, [permissionsData]);

  const filteredPermissionNames = useMemo(() => {
    return Object.values(groupedPermissions).flat().map((p) => p.name);
  }, [groupedPermissions]);

  // Fetch role data for editing
  const { mutate: fetchRole } = useMutation({
    mutationFn: (roleId: number | string) => rolesApi.getById(roleId),
    onSuccess: (data) => {
      form.reset({
        name: data.name || '',
        name_en: data.name_en || '',
        name_ru: data.name_ru || '',
        status: data.status || 'ACTIVE',
        type: data.type || 'MANAGER',
        rate: data.rate ?? undefined,
        permissions: data.permissions?.map((item) => item.name) || [],
      });
    },
    onError: (err: { message?: string }) => {
      toast.error(t('loadError', { error: err?.message || 'Unknown error' }));
    },
  });

  // Create mutation
  const { mutate: createRole, isPending: isCreating } = useMutation({
    mutationFn: rolesApi.create,
    onSuccess: () => {
      toast.success(t('createdSuccess', { entity: t('role') }));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
      form.reset();
      setOpen(false);
    },
    onError: (err: { message?: string }) => {
      toast.error(t('createError', { error: err?.message || 'Unknown error' }));
    },
  });

  // Update mutation
  const { mutate: updateRole, isPending: isUpdating } = useMutation({
    mutationFn: ({ roleId, data }: { roleId: number | string; data: RoleFormValues }) =>
      rolesApi.update({ id: roleId, data }),
    onSuccess: () => {
      toast.success(t('updatedSuccess', { entity: t('role') }));
      queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
      form.reset();
      setOpen(false);
    },
    onError: (err: { message?: string }) => {
      toast.error(t('updateError', { error: err?.message || 'Unknown error' }));
    },
  });

  const isPending = isCreating || isUpdating;

  useEffect(() => {
    if (open && id) {
      fetchRole(id);
    }
  }, [open, id, fetchRole]);

  const onSubmit = (values: RoleFormValues) => {
    if (isEdit) {
      updateRole({ roleId: id!, data: values });
    } else {
      createRole(values);
    }
  };

  const handleSaveAndCreate = (values: RoleFormValues) => {
    createRole(values, {
      onSuccess: () => {
        toast.success(t('createdSuccess', { entity: t('role') }));
        queryClient.invalidateQueries({ queryKey: rolesQueryKeys.lists() });
        form.reset();
      },
    });
  };

  const togglePermission = (permName: string) => {
    const current = form.getValues('permissions') || [];
    const updated = current.includes(permName)
      ? current.filter((p) => p !== permName)
      : [...current, permName];
    form.setValue('permissions', updated, { shouldDirty: true });
  };

  const selectAll = () => {
    form.setValue('permissions', allPermissionNames, { shouldDirty: true });
  };

  const deselectAll = () => {
    form.setValue('permissions', [], { shouldDirty: true });
  };

  const selectFiltered = () => {
    const current = form.getValues('permissions') || [];
    const newPerms = [...new Set([...current, ...filteredPermissionNames])];
    form.setValue('permissions', newPerms, { shouldDirty: true });
  };

  const deselectFiltered = () => {
    const current = form.getValues('permissions') || [];
    const updated = current.filter((p) => !filteredPermissionNames.includes(p));
    form.setValue('permissions', updated, { shouldDirty: true });
  };

  return {
    form,
    open,
    setOpen,
    isEdit,
    isPending,
    searchQuery,
    setSearchQuery,
    selectedPermissions,
    isLoadingPermissions,
    groupedPermissions,
    onSubmit,
    handleSaveAndCreate,
    togglePermission,
    selectAll,
    deselectAll,
    selectFiltered,
    deselectFiltered,
  };
}
